import React, { useState, useEffect } from "react";
import SuccessModal from "../connections/SuccessModal";
import ErrorModal from "../connections/ErrorModal";
import Modal from "../../elements/Modal";
import ConsolDropdown from "../../elements/ConsolDropdown";
import InputGroup from "../../elements/InputGroup";
import { Button } from "../../elements/Button";
import buttoncss from "../../css/button.module.css";
import commoncss from "../../css/common.module.css";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import {
  getRptLedgerOptionsApiHandler,
  setRptLineLedgerApiHandler,
  getManualRelatedPartyDetailsApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

export default function RptLineLedgerModal({
  isOpen,
  onClose,
  row,
  tableTitle = "Transactions",
  heading = "",
  onSuccess = () => {},
}) {
  const projectID = localStorage.getItem("project_id");
  const defaultCompanyID =
    row?.companyID ||
    localStorage.getItem("company_id") ||
    localStorage.getItem("companyID") ||
    localStorage.getItem("company") ||
    "";

  const [relatedPartyName, setRelatedPartyName] = useState("");
  const [ledgerCode, setLedgerCode] = useState("");
  const [ledgerName, setLedgerName] = useState("");
  const [levelOne, setLevelOne] = useState("");
  const [levelTwo, setLevelTwo] = useState("");
  const [levelThree, setLevelThree] = useState("");
  const [companyID, setCompanyID] = useState(defaultCompanyID);

  const [ledgerOptionsList, setLedgerOptionsList] = useState([]);
  const [relatedPartyOptions, setRelatedPartyOptions] = useState([]);
  const [ledgerCodeOptions, setLedgerCodeOptions] = useState([]);
  const [ledgerNameOptions, setLedgerNameOptions] = useState([]);
  const [levelOneOptions, setLevelOneOptions] = useState([]);
  const [levelTwoOptions, setLevelTwoOptions] = useState([]);
  const [levelThreeOptions, setLevelThreeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savedPayload, setSavedPayload] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const initialParty =
        row?.relatedPartyName ||
        row?.partyName ||
        row?.related_party_name ||
        (row?.particular !== heading ? row?.particular : "") ||
        "";
      setRelatedPartyName(initialParty);
      setLedgerCode(row?.ledgerCode || "");
      setLedgerName(row?.ledgerName || "");
      setLevelOne(row?.levelOne || "");
      setLevelTwo(row?.levelTwo || "");
      setLevelThree(row?.levelThree || "");
      setCompanyID(
        row?.companyID ||
          localStorage.getItem("company_id") ||
          localStorage.getItem("companyID") ||
          localStorage.getItem("company") ||
          ""
      );
      fetchOptions();
    }
  }, [isOpen, row]);

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => {
        setShowSuccessModal(false);
        if (savedPayload) {
          onSuccess(savedPayload);
        }
        onClose();
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal, savedPayload, onSuccess, onClose]);

  useEffect(() => {
    let timer;
    if (showErrorModal) {
      timer = setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showErrorModal]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const [ledgerRes, manualRes] = await Promise.allSettled([
        getRptLedgerOptionsApiHandler({ projectID }),
        getManualRelatedPartyDetailsApiHandler({ projectID }),
      ]);

      const raw =
        ledgerRes.status === "fulfilled"
          ? ledgerRes.value?.data?.data ||
            ledgerRes.value?.data?.options ||
            ledgerRes.value?.data?.ledgerOptions ||
            ledgerRes.value?.data ||
            []
          : [];

      let list = [];
      let parties = [];
      let lvl1 = [];
      let lvl2 = [];
      let lvl3 = [];

      if (Array.isArray(raw)) {
        list = raw;
        raw.forEach((item) => {
          const party =
            item.relatedPartyName || item.related_party_name || item.partyName;
          if (party && !parties.includes(party)) parties.push(party);

          const l1 = item.levelOne || item.level1 || item.level_one;
          if (l1 && !lvl1.includes(l1)) lvl1.push(l1);

          const l2 = item.levelTwo || item.level2 || item.level_two;
          if (l2 && !lvl2.includes(l2)) lvl2.push(l2);

          const l3 = item.levelThree || item.level3 || item.level_three;
          if (l3 && !lvl3.includes(l3)) lvl3.push(l3);
        });
      } else if (raw && typeof raw === "object") {
        list = raw.ledgers || raw.ledgerOptions || raw.data || [];
        parties = raw.relatedParties || raw.relatedPartyNames || [];
        lvl1 = raw.levelOne || raw.level1 || [];
        lvl2 = raw.levelTwo || raw.level2 || [];
        lvl3 = raw.levelThree || raw.level3 || [];
      }

      setLedgerOptionsList(list);

      // Populate Related Party Name dropdown from getManualRelatedPartyDetails API
      let partyOptionsList = [];
      const seenParties = new Set();

      if (manualRes.status === "fulfilled") {
        const manualData = manualRes.value?.data;
        const manualPartyList =
          manualData?.relatedPartyNameList ||
          manualData?.data?.relatedPartyNameList ||
          manualData?.parties ||
          manualData?.data ||
          [];

        if (Array.isArray(manualPartyList)) {
          manualPartyList.forEach((item) => {
            const partyName =
              typeof item === "string"
                ? item
                : item?.name ||
                  item?.relatedPartyName ||
                  item?.label ||
                  item?.value ||
                  "";
            if (partyName && !seenParties.has(partyName.toLowerCase())) {
              seenParties.add(partyName.toLowerCase());
              partyOptionsList.push({ label: partyName, value: partyName });
            }
          });
        }
      }

      // Fallback to parties found in ledger options if manual endpoint returned empty
      if (partyOptionsList.length === 0) {
        parties.forEach((p) => {
          const name = typeof p === "string" ? p : p?.label || p?.value || "";
          if (name && !seenParties.has(name.toLowerCase())) {
            seenParties.add(name.toLowerCase());
            partyOptionsList.push({ label: name, value: name });
          }
        });
      }

      setRelatedPartyOptions(partyOptionsList);

      const codeOpts = [];
      const nameOpts = [];
      const seenCodes = new Set();
      const seenNames = new Set();

      list.forEach((item) => {
        const code =
          item.ledgerCode !== undefined && item.ledgerCode !== null
            ? String(item.ledgerCode)
            : "";
        const name =
          item.ledgerName !== undefined && item.ledgerName !== null
            ? String(item.ledgerName)
            : "";

        if (code && !seenCodes.has(code)) {
          seenCodes.add(code);
          codeOpts.push({
            label: code,
            value: code,
            data: item,
          });
        }
        if (name && !seenNames.has(name.toLowerCase())) {
          seenNames.add(name.toLowerCase());
          nameOpts.push({
            label: name,
            value: name,
            data: item,
          });
        }
      });
      setLedgerCodeOptions(codeOpts);
      setLedgerNameOptions(nameOpts);

      setLevelOneOptions(
        lvl1.map((v) =>
          typeof v === "string" ? { label: v, value: v } : v
        )
      );
      setLevelTwoOptions(
        lvl2.map((v) =>
          typeof v === "string" ? { label: v, value: v } : v
        )
      );
      setLevelThreeOptions(
        lvl3.map((v) =>
          typeof v === "string" ? { label: v, value: v } : v
        )
      );
    } catch (err) {
      console.error("Failed to fetch RPT ledger options", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLedgerCodeSelect = (selected) => {
    const val = selected ? selected.value : "";
    setLedgerCode(val);

    const match =
      selected?.data ||
      ledgerOptionsList.find(
        (item) =>
          String(item.ledgerCode).toLowerCase() === String(val).toLowerCase() ||
          String(item.ledgerCode).toLowerCase() ===
            String(selected?.label).toLowerCase()
      );
    if (match) {
      if (match.ledgerCode != null) setLedgerCode(String(match.ledgerCode));
      if (match.ledgerName != null) setLedgerName(String(match.ledgerName));
      if (match.levelOne || match.level1 || match.level_one)
        setLevelOne(match.levelOne || match.level1 || match.level_one);
      if (match.levelTwo || match.level2 || match.level_two)
        setLevelTwo(match.levelTwo || match.level2 || match.level_two);
      if (match.levelThree || match.level3 || match.level_three)
        setLevelThree(match.levelThree || match.level3 || match.level_three);
      if (match.companyID) setCompanyID(match.companyID);
    }
  };

  const handleLedgerNameSelect = (selected) => {
    const val = selected ? selected.value : "";
    setLedgerName(val);

    const match =
      selected?.data ||
      ledgerOptionsList.find(
        (item) =>
          String(item.ledgerName).toLowerCase() === String(val).toLowerCase() ||
          String(item.ledgerName).toLowerCase() ===
            String(selected?.label).toLowerCase()
      );
    if (match) {
      if (match.ledgerCode != null) setLedgerCode(String(match.ledgerCode));
      if (match.ledgerName != null) setLedgerName(String(match.ledgerName));
      if (match.levelOne || match.level1 || match.level_one)
        setLevelOne(match.levelOne || match.level1 || match.level_one);
      if (match.levelTwo || match.level2 || match.level_two)
        setLevelTwo(match.levelTwo || match.level2 || match.level_two);
      if (match.levelThree || match.level3 || match.level_three)
        setLevelThree(match.levelThree || match.level3 || match.level_three);
      if (match.companyID) setCompanyID(match.companyID);
    }
  };

  const handleSave = async () => {
    if (!relatedPartyName && !ledgerCode && !ledgerName) {
      setErrorMessage("Please fill in at least Related Party Name or Ledger Details");
      setShowErrorModal(true);
      return;
    }

    setSaving(true);
    const activeHeading =
      heading ||
      row?.heading ||
      (row?.particular !== relatedPartyName ? row?.particular : "") ||
      "";

    const payload = {
      tableTitle: tableTitle || "Transactions",
      heading: activeHeading,
      relatedPartyName: relatedPartyName || "",
      ledgerCode: ledgerCode || "",
      ledgerName: ledgerName || "",
      levelOne: levelOne || "",
      levelTwo: levelTwo || "",
      levelThree: levelThree || "",
      companyID: companyID || "",
    };

    try {
      const response = await setRptLineLedgerApiHandler(
        { projectID },
        payload
      );
      if (response?.data?.success !== false) {
        setSavedPayload(payload);
        setSuccessMessage("Line ledger details updated successfully");
        setShowSuccessModal(true);
      } else {
        setErrorMessage(
          response?.data?.message ||
            response?.data?.error ||
            "Failed to update line ledger details"
        );
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error("Error setting RPT line ledger:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update line ledger details"
      );
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
      width="720px"
      title="RPT Configuration"
      bodycss={commoncss.unsetPadding}
      headerActions={
        <CloseIcon
          onClick={onClose}
          className="clickable"
          style={{ cursor: "pointer" }}
        />
      }
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            onClick={onClose}
            label="Cancel"
          />
          <Button
            btnColor={buttoncss.primary}
            onClick={handleSave}
            label={saving ? "Saving..." : "Save"}
            disabled={saving}
          />
        </>
      }
      onClose={onClose}
    >
      <div
        className={`${commoncss.pd1530} rpt-ledger-modal-content`}
        style={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        <style>{`
          .rpt-ledger-modal-content {
            padding-bottom: 20px !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }
          .rpt-ledger-modal-content::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .rpt-ledger-modal-content .rpt-row {
            position: relative;
            transition: z-index 0.1s ease;
          }
          .rpt-ledger-modal-content .rpt-row:has([class*="open"]),
          .rpt-ledger-modal-content .rpt-row:focus-within {
            z-index: 1000 !important;
          }
          .rpt-ledger-modal-content [class*="dropdown_menu"]:not([class*="open"]) {
            display: none !important;
            max-height: 0 !important;
            height: 0 !important;
            opacity: 0 !important;
            pointer-events: none !important;
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .rpt-ledger-modal-content [class*="dropdown_menu"][class*="open"] {
            display: block !important;
            max-height: 136px !important;
            opacity: 1 !important;
            pointer-events: auto !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            background-color: #ffffff !important;
            border: 1px solid #c2e6d1 !important;
            border-radius: 10px !important;
            box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.15) !important;
          }
          .rpt-ledger-modal-content [class*="dropdown_menu"][class*="open"]::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .rpt-ledger-modal-content [class*="dropdown_item"] {
            height: 38px !important;
            min-height: 38px !important;
            max-height: 38px !important;
            padding: 0 12px !important;
            margin: 3px 6px !important;
            display: flex !important;
            align-items: center !important;
            box-sizing: border-box !important;
            white-space: nowrap !important;
            text-overflow: ellipsis !important;
            overflow: hidden !important;
          }
        `}</style>

          {/* Form fields styled identically to Configure Mapping in Investment */}
          <div className="row rpt-row" style={{ position: "relative", zIndex: 10, marginBottom: "8px" }}>
            <div className="col-md-12">
              <InputGroup
                label="Related Party Name"
                inputs={[
                  <ConsolDropdown
                    options={relatedPartyOptions}
                    selected={
                      relatedPartyName
                        ? relatedPartyOptions.find(
                            (o) =>
                              String(o.value).toLowerCase() ===
                              String(relatedPartyName).toLowerCase()
                          ) || {
                            label: relatedPartyName,
                            value: relatedPartyName,
                          }
                        : null
                    }
                    onSelect={(sel) =>
                      setRelatedPartyName(sel ? sel.value : "")
                    }
                    placeHolder="Select or enter Related Party Name"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
          </div>

          <div className="row rpt-row" style={{ position: "relative", zIndex: 9, marginBottom: "8px" }}>
            <div className="col-md-6" style={{ paddingRight: "6px" }}>
              <InputGroup
                label="Ledger Code"
                inputs={[
                  <ConsolDropdown
                    options={ledgerCodeOptions}
                    selected={
                      ledgerCode
                        ? ledgerCodeOptions.find(
                            (o) => String(o.value) === String(ledgerCode)
                          ) || {
                            label: ledgerCode,
                            value: ledgerCode,
                          }
                        : null
                    }
                    onSelect={handleLedgerCodeSelect}
                    placeHolder="Select or enter Ledger Code"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
            <div className="col-md-6" style={{ paddingLeft: "6px" }}>
              <InputGroup
                label="Ledger Name"
                inputs={[
                  <ConsolDropdown
                    options={ledgerNameOptions}
                    selected={
                      ledgerName
                        ? ledgerNameOptions.find(
                            (o) =>
                              String(o.value).toLowerCase() ===
                              String(ledgerName).toLowerCase()
                          ) || {
                            label: ledgerName,
                            value: ledgerName,
                          }
                        : null
                    }
                    onSelect={handleLedgerNameSelect}
                    placeHolder="Select or enter Ledger Name"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
          </div>

          <div className="row rpt-row" style={{ position: "relative", zIndex: 8, marginBottom: "8px" }}>
            <div className="col-md-6" style={{ paddingRight: "6px" }}>
              <InputGroup
                label="Level 1"
                inputs={[
                  <ConsolDropdown
                    options={levelOneOptions}
                    selected={
                      levelOne
                        ? levelOneOptions.find(
                            (o) =>
                              String(o.value).toLowerCase() ===
                              String(levelOne).toLowerCase()
                          ) || {
                            label: levelOne,
                            value: levelOne,
                          }
                        : null
                    }
                    onSelect={(sel) => setLevelOne(sel ? sel.value : "")}
                    placeHolder="Select or enter Level 1"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
            <div className="col-md-6" style={{ paddingLeft: "6px" }}>
              <InputGroup
                label="Level 2"
                inputs={[
                  <ConsolDropdown
                    options={levelTwoOptions}
                    selected={
                      levelTwo
                        ? levelTwoOptions.find(
                            (o) =>
                              String(o.value).toLowerCase() ===
                              String(levelTwo).toLowerCase()
                          ) || {
                            label: levelTwo,
                            value: levelTwo,
                          }
                        : null
                    }
                    onSelect={(sel) => setLevelTwo(sel ? sel.value : "")}
                    placeHolder="Select or enter Level 2"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
          </div>

          <div className="row rpt-row" style={{ position: "relative", zIndex: 7 }}>
            <div className="col-md-12">
              <InputGroup
                label="Level 3"
                inputs={[
                  <ConsolDropdown
                    options={levelThreeOptions}
                    selected={
                      levelThree
                        ? levelThreeOptions.find(
                            (o) =>
                              String(o.value).toLowerCase() ===
                              String(levelThree).toLowerCase()
                          ) || {
                            label: levelThree,
                            value: levelThree,
                          }
                        : null
                    }
                    onSelect={(sel) => setLevelThree(sel ? sel.value : "")}
                    placeHolder="Select or enter Level 3"
                    className={commoncss.placeholderStyle}
                    isEditable={true}
                    openUpward={true}
                    disabled={loading}
                  />,
                ]}
              />
            </div>
          </div>
      </div>
    </Modal>

      {showSuccessModal && (
        <SuccessModal
          handleClose={() => {
            setShowSuccessModal(false);
            if (savedPayload) {
              onSuccess(savedPayload);
            }
            onClose();
          }}
          setSuccessModal={setShowSuccessModal}
          setSuccessMessage={setSuccessMessage}
          successMessage={successMessage}
        />
      )}

      {showErrorModal && (
        <ErrorModal
          handleClose={() => setShowErrorModal(false)}
          setErrorModal={setShowErrorModal}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
        />
      )}
    </>
  );
}
